/**
 * @fileoverview `EventPanelComposer` (WEB-764) — the M5 panel composer that
 * fills `EventDecisionPanel`'s `children` slot with the three M3 satellites
 * (mini-map, venue snippet, transit rows) from **one** getting-there
 * derivation.
 *
 * ## Why this file exists
 *
 * `EventMiniMap` and `TransitPanelRows` are independent molecules. Each takes
 * its data as props and derives nothing. That was deliberate — and it is why
 * agreeing on screen is a composer responsibility. Two separate
 * `buildGettingThereOptionsFromLocation` calls would usually agree, but not
 * always: the Places merge is lazy, so a map rendered before the merge
 * resolves and rows rendered after it would disagree about how many entries
 * exist. The design stacks them directly on top of each other. A mismatch is
 * visible at a glance.
 *
 * This file calls `resolvePanelGettingThereOptions` (one build, and one merge
 * where Places applies) exactly once per render pass, then feeds the same
 * array — and a count taken through `resolveTransitPanelRows` — into both
 * satellites. Neither satellite re-implements the two-row cap.
 *
 * ## Places arrives late
 *
 * The route starts Places as a promise (FRE-1292). Until it resolves, the
 * panel paints from Location columns alone — the same fallback
 * `EventDetailTemplate` already uses for `GettingThereCard`. When the
 * promise resolves, **both** satellites re-render from the merged array in
 * the same commit. The map never sits on pre-merge dots while the rows
 * list the merged set.
 *
 * ## View + Connected
 *
 * `EventPanelComposerView` is pure props (already-resolved options + chroma)
 * and is what Storybook + the count-parity tests target.
 * `EventPanelComposer` (Connected) owns `useTheme`, the single builder call,
 * and the Suspense unwrap of the Places promise.
 *
 * `'use client'` is required: `useTheme`, `use()`, and the three satellites
 * (plus `EventDecisionPanel`) are already client modules. A Server Component
 * that imported `PANEL_SECTION_CLASS` from those modules would receive a
 * client-reference proxy — the same trap `EventMiniMap` documents.
 */
'use client';

import { Suspense, use, type ReactElement } from 'react';

import type { CityCode } from '@loci/city-editions';
import { useTheme } from '@loci/ui';

import { EventMiniMap } from '@/components/molecules/EventMiniMap';
import { resolveTransitPanelRows, TransitPanelRows } from '@/components/molecules/TransitPanelRows';
import {
  resolveVenuePanelSnippetSource,
  VenuePanelSnippet,
} from '@/components/molecules/VenuePanelSnippet';
import { EventDecisionPanel } from '@/components/organisms/EventDecisionPanel';
import type { GettingThereOption } from '@/components/organisms/GettingThereCard';
import { getEventLexicon } from '@/config/eventLexicon';
import { mergeGettingThereOptions } from '@/lib/gettingThere/mergeGettingThereOptions';
import { resolvePanelGettingThereOptions } from '@/lib/gettingThere/resolvePanelGettingThereOptions';
import type { EventDetailV2 } from '@/types/eventDetail';
import { getCategoryChroma, type CategoryChroma } from '@/utils/categoryColors';

export interface EventPanelComposerViewProps {
  event: EventDetailV2;
  categorySlug?: string | null;
  cityCode: CityCode;
  /**
   * Already built (and, where available, Places-merged) by the caller.
   * The View never calls the builder.
   */
  gettingThereOptions: readonly GettingThereOption[];
  /** Caller-resolved category chroma (`getCategoryChroma`) — never derived here. */
  chroma: Pick<CategoryChroma, 'cat' | 'catLine'>;
  /** WEB-726: ticket-click analytics from the live event route. */
  onCtaActivate?: () => void;
  className?: string;
}

/**
 * Pure composed panel: decision card + the three M3 satellites, all reading
 * the same `gettingThereOptions` array. No hooks, no builder call.
 */
export function EventPanelComposerView({
  event,
  categorySlug,
  cityCode,
  gettingThereOptions,
  chroma,
  onCtaActivate,
  className,
}: EventPanelComposerViewProps): ReactElement {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : '';
  const lexicon = getEventLexicon(slug);
  const venue = resolveVenuePanelSnippetSource(event);
  const location = event.location ?? null;
  const satelliteCount = resolveTransitPanelRows(gettingThereOptions).length;

  return (
    <EventDecisionPanel
      event={event}
      categorySlug={categorySlug}
      onCtaActivate={onCtaActivate}
      className={className}
    >
      <EventMiniMap venue={location} gettingThereCount={satelliteCount} chroma={chroma} />
      <VenuePanelSnippet
        venueName={venue.venueName}
        venueSlug={venue.venueSlug}
        stats={venue.stats}
        kicker={lexicon.venueKicker}
        cityCode={cityCode}
      />
      <TransitPanelRows options={gettingThereOptions} />
    </EventDecisionPanel>
  );
}

export interface EventPanelComposerProps {
  event: EventDetailV2;
  categorySlug?: string | null;
  cityCode: CityCode;
  /**
   * Already-resolved Places rows. Prefer for tests/stories; the route passes
   * {@link placesGettingThereOptionsPromise}.
   */
  placesGettingThereOptions?: readonly GettingThereOption[] | null;
  /**
   * SSR Places promise. Unwrapped inside a nested Suspense boundary so the
   * Location-only panel paints first and both satellites update together
   * when the merge arrives.
   */
  placesGettingThereOptionsPromise?: Promise<readonly GettingThereOption[] | null>;
  /** Forwarded to the decision-panel CTA (ticket-click analytics). */
  onCtaActivate?: () => void;
  className?: string;
}

interface ComposerWithPlacesProps {
  event: EventDetailV2;
  categorySlug?: string | null;
  cityCode: CityCode;
  locationOptions: readonly GettingThereOption[];
  placesPromise: Promise<readonly GettingThereOption[] | null>;
  chroma: Pick<CategoryChroma, 'cat' | 'catLine'>;
  onCtaActivate?: () => void;
  className?: string;
}

/**
 * Merge the parent's already-built Location options with the unwrapped
 * Places promise. Must not call `buildGettingThereOptionsFromLocation`
 * again — that second call is the exact disagreement this composer exists
 * to prevent.
 */
function applyPlacesMerge(
  locationOptions: readonly GettingThereOption[],
  places: readonly GettingThereOption[] | null,
): readonly GettingThereOption[] {
  return places && places.length > 0
    ? mergeGettingThereOptions(locationOptions, places)
    : locationOptions;
}

function EventPanelComposerWithPlaces({
  event,
  categorySlug,
  cityCode,
  locationOptions,
  placesPromise,
  chroma,
  onCtaActivate,
  className,
}: ComposerWithPlacesProps): ReactElement {
  const gettingThereOptions = applyPlacesMerge(locationOptions, use(placesPromise));

  return (
    <EventPanelComposerView
      event={event}
      categorySlug={categorySlug}
      cityCode={cityCode}
      gettingThereOptions={gettingThereOptions}
      chroma={chroma}
      onCtaActivate={onCtaActivate}
      className={className}
    />
  );
}

/**
 * `EventPanelComposer` — Connected. One builder call per render pass; one
 * merge where Places applies. Theme → chroma lives here so the View stays
 * hook-free.
 */
export function EventPanelComposer({
  event,
  categorySlug,
  cityCode,
  placesGettingThereOptions = null,
  placesGettingThereOptionsPromise,
  onCtaActivate,
  className,
}: EventPanelComposerProps): ReactElement {
  const { mode } = useTheme();
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : '';
  const chroma = getCategoryChroma(slug, mode === 'dark' ? 'dark' : 'light');
  const location = event.location ?? null;

  // Exactly one Location-backed build per render pass. The Suspense child
  // (and the sync Places branch) reuse this array — they merge, they do
  // not rebuild.
  const locationOptions = resolvePanelGettingThereOptions(location);

  if (placesGettingThereOptionsPromise) {
    return (
      <Suspense
        fallback={
          <EventPanelComposerView
            event={event}
            categorySlug={categorySlug}
            cityCode={cityCode}
            gettingThereOptions={locationOptions}
            chroma={chroma}
            onCtaActivate={onCtaActivate}
            className={className}
          />
        }
      >
        <EventPanelComposerWithPlaces
          event={event}
          categorySlug={categorySlug}
          cityCode={cityCode}
          locationOptions={locationOptions}
          placesPromise={placesGettingThereOptionsPromise}
          chroma={chroma}
          onCtaActivate={onCtaActivate}
          className={className}
        />
      </Suspense>
    );
  }

  const gettingThereOptions = applyPlacesMerge(locationOptions, placesGettingThereOptions);

  return (
    <EventPanelComposerView
      event={event}
      categorySlug={categorySlug}
      cityCode={cityCode}
      gettingThereOptions={gettingThereOptions}
      chroma={chroma}
      onCtaActivate={onCtaActivate}
      className={className}
    />
  );
}
